import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule, DatePipe } from '@angular/common';
import { MaterialModule } from 'src/app/material.module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LookupService } from 'src/app/services/lookup.service';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { firstValueFrom, Subject, takeUntil, startWith, map, Observable, debounceTime } from 'rxjs';
import { TablerIconsModule } from 'angular-tabler-icons';
import { ReportsService } from './reports.service';
import { DATE_FORMAT } from 'src/app/common/constant/constants.type';
import { IPatientReport, IPagedPatientReportResult } from './ireports';
import { HttpClient } from '@angular/common/http';
import { IHospitalInfo } from '../hospital/ihospital-info';
import { HospitalStateService } from 'src/app/services/hospitalstate.service';
import { environment } from 'src/environments/environment';
import { BaseRespone } from 'src/app/models/common.response';
import { LoginService } from 'src/app/login.service';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MY_FORMATS } from 'src/app/common/dateFormat';
import { ToastrService } from 'ngx-toastr';
import { PatientNameUtils } from 'src/app/common/utils/patient-name.utils';

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    TranslateModule,
    TablerIconsModule
  ],
  providers: [
    DatePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss'
})
export class ReportsComponent implements OnInit, OnDestroy {
  doctorControls = new FormControl('');
  doctorslist: CommonDropDown[] = [];
  private destroy$ = new Subject<void>();
  ReportForm: FormGroup;
  minDate: Date;
  maxDate: Date = new Date();
  title: string;
  hospitalInfo: IHospitalInfo;
  staticimagepath = environment.imagePath;
  selectedDoctors: CommonDropDown[] = [];
  isDoctor = false;
  loggedInDoctorId: number | null = null;
  filteredDoctorList$: Observable<CommonDropDown[]>;
  showAddOptionforDoctorList = false;

  constructor(
    public dialogRef: MatDialogRef<ReportsComponent>,
    private _lookupService: LookupService,
    private _reportService: ReportsService,
    private datePipe: DatePipe,
    private fb: FormBuilder,
    private http: HttpClient,
    private hospitalData: HospitalStateService,
    private loginservice: LoginService,
    private toastr: ToastrService,
    private translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { this.title = data.title; }

  ngOnInit(): void {
    this.hospitalInfo = this.hospitalData.getHospital();
    this.isDoctor = this.loginservice.isDoctor();

    this.ReportForm = this.fb.group({
      doctorIds: [[], Validators.required],
      startDate: ['', Validators.required],
      endDate: ['', Validators.required]
    });

    if (this.isDoctor) {
      this.loggedInDoctorId = this.loginservice.getUserId();
      this.ReportForm.patchValue({
        doctorIds: [this.loggedInDoctorId]
      });

      this._lookupService.doctors$
        .pipe(takeUntil(this.destroy$))
        .subscribe(list => {
          const doc = list.find(d => d.id === this.loggedInDoctorId);
          if (doc) {
            this.selectedDoctors = [doc];
          }
        });
      return;
    }

    this._lookupService.getDoctors()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.doctorslist = data || [];

        this.filteredDoctorList$ = this.doctorControls.valueChanges.pipe(
          startWith(''),
          debounceTime(300),
          map((value) => {
            const val = typeof value === 'string' ? value : '';
            const filtered = this._filterDoctorContent(val);
            this.showAddOptionforDoctorList =
              !!val && !filtered.some((x) => x.name.toLowerCase() === val.toLowerCase());
            return filtered;
          })
        );
      });

    // Sync selected doctors with FormGroup
    this.doctorControls.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const selectedIds = this.selectedDoctors.map(d => d.id);
        this.ReportForm.get('doctorIds')?.setValue(selectedIds, { emitEvent: false });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onCloseDialog(): void {
    this.dialogRef.close();
  }

  private _filterDoctorContent(value: string): CommonDropDown[] {
    const filterValue = value.toLowerCase();
    return this.doctorslist.filter(option =>
      option.name.toLowerCase().includes(filterValue)
    );
  }

  PrintReport(): void {
    const formValue = this.ReportForm.getRawValue();

    if (formValue.startDate && formValue.endDate) {
      const startDate = new Date(formValue.startDate);
      const endDate = new Date(formValue.endDate);

      if (endDate < startDate) {
        this.toastr.error(this.translate.instant('DATE_COMPARE_START_END'));
        return;
      }
    }

    const requestData = {
      ...formValue,
      startDate: formValue.startDate && this.datePipe.transform(formValue.startDate, DATE_FORMAT),
      endDate: formValue.endDate && this.datePipe.transform(formValue.endDate, DATE_FORMAT),
    };

    let apiCall$ = this._reportService.getPatientOpdReport(requestData);

    apiCall$.subscribe(async (response: BaseRespone<IPagedPatientReportResult>) => {
      if (response.isSuccess && response.result) {
        this.dialogRef.close(requestData);
        const reportItems: IPatientReport[] = response.result.patientReportDetails;
        if (reportItems.length === 0) {
          this.toastr.error(this.translate.instant('LABEL.NO_DATA'));
          return;
        }

        const content = await this.createReportContent(reportItems, this.title, this.ReportForm);
        this.openPrintWindow(content);
      }
    });


  }

  private async createReportContent(items: IPatientReport[], title: string, form: FormGroup): Promise<string> {
    let template =
      (await firstValueFrom(
        this.http.get('assets/print-report-template.html', { responseType: 'text' })
      )) || '';

    const rowsHtml = items
      .map(
        (p, index) => `
        <div class="row" style="display:flex; text-align:left; border-bottom:1px solid #ccc; padding:6px 10px;">
          <div style="flex:0.5;">${index + 1}</div>
          <div style="flex:2;">${this.datePipe.transform(p.opdDate, 'dd-MM-yyyy')}</div>
          <div style="flex:2;">${p.opdNo ?? ''}</div>
          <div style="flex:3;">${PatientNameUtils.formatFullName(p as any, p.patientName ?? '')}</div>
          <div style="flex:2;">${p.mobile ?? ''}</div>
          <div style="flex:2;">${p.address ?? ''}</div>
          <div style="flex:3;">${p.diagnosis ?? ''}</div>
        </div>`
      )
      .join('');

    const tableHtml = `

    <div style="text-align:center; margin-bottom:10px;">
      <h2>${title}</h2>
    </div>
    <div style="margin-bottom:10px;">
      <h4>Search for :- ${form.value.startDate ? this.datePipe.transform(form.value.startDate, 'dd-MM-yyyy') : ''} to ${form.value.endDate ? this.datePipe.transform(form.value.endDate, 'dd-MM-yyyy') : ''}</h4>
    </div>
      <div class="report-table" style="display:flex; flex-direction:column; border:1px solid #999; border-radius:6px; overflow:hidden;">

        <div class="header" style="display:flex; background:#f2f2f2; font-weight:600; text-align:left; border-bottom:1px solid #ccc; padding:6px 10px;">
          <div style="flex:0.5;">No</div>
          <div style="flex:2;">OPD Date</div>
          <div style="flex:2;">OPD No</div>
          <div style="flex:3;">Patient Name</div>
          <div style="flex:2;">Mobile</div>
          <div style="flex:2;">Address</div>
          <div style="flex:3;">Diagnosis</div>
        </div>

        ${rowsHtml}
      </div>
    `;
    const fullImagePath = this.hospitalInfo?.logo ? `${this.staticimagepath}${this.hospitalInfo.logo}` : null;
    const defaultLogo = `${window.location.origin}/assets/images/svgs/hospital.png`;

    return template
      .replace('{{reportTitle}}', title)
      .replace('{{hospitalName}}', this.hospitalInfo.name ?? '')
      .replace('{{hospitalAddress}}', `${this.hospitalInfo.address ?? ''}, ${this.hospitalInfo.city ?? ''}`)
      .replace('{{phone}}', this.hospitalInfo.phone ?? '')
      .replace('{{reportSections}}', tableHtml)
      .replace('{{doctorName}}', this.selectedDoctors.map(d => d.name).join(', '))
      .replace('{{logo}}', `<img src="${fullImagePath || defaultLogo}" class="logo" style="width:50px" onerror="this.onerror=null;this.src='${defaultLogo}'"/>`);
  }

  private openPrintWindow(content: string): void {

    let iframe = document.getElementById('print-frame') as HTMLIFrameElement;

    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = 'print-frame';
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
    }

    const doc = iframe.contentWindow?.document;

    if (!doc) {
      return;
    }

    doc.open();
    doc.write(content);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow?.print();
    }, 300);
  }

  onDoctorToggle(doc: CommonDropDown): void {
    const index = this.selectedDoctors.findIndex(d => d.id === doc.id);

    if (index > -1) {
      this.selectedDoctors.splice(index, 1);
    } else {
      this.selectedDoctors.push(doc);
    }

    this.ReportForm.get('doctorIds')?.setValue(this.selectedDoctors.map(d => d.id));

    this.doctorControls.setValue(
      this.selectedDoctors.map(d => d.name).join(', '),
      { emitEvent: false }
    );
  }

  isDoctorSelected(id: number): boolean {
    return this.selectedDoctors.some(d => d.id === id);
  }
}
